import React, { useEffect, useRef } from 'react';
import { toast } from 'react-toastify';

export default function AudioChannelPlayer(props: Props) {
	const container = useRef<HTMLDivElement | null>(null)
	
	useEffect(() => {
		if (!container.current)
			return;
		
		try {
			const fileUrl = URL.createObjectURL(props.file);
			const audio = new Audio();
			const ctx = getContext();

			audio.controls = true;
			audio.src = fileUrl;
			audio.style.width = '100%';
			
			const source = ctx.createMediaElementSource(audio);

			// using 3 channels here to make sure any channels beyond
			// 2 don't end up in channel 2 (not fully sure this actually
			// needed but hey it's easy)
			const splitter = ctx.createChannelSplitter(3);
			const merger = ctx.createChannelMerger(2);

			source.connect(splitter);

			const target = props.channel - 1;
			splitter.connect(merger, target, 0);
			splitter.connect(merger, target, 1);

			merger.connect(ctx.destination);

			container.current.appendChild(audio);

			// need to resume the context manually since some browsers 
			// (Safari) won't resume automatically and will only resume 
			// in response to a user interaction
			audio.addEventListener('click', () => {
				if (ctx.state !== 'running' && ctx.state !== 'closed') {
					ctx.resume();
				}
			});
			
			return () => {
				try {
					audio.pause();
					audio.remove();
					URL.revokeObjectURL(fileUrl);
					ctx.close();
				} catch (e) {
					console.log('Error tearing down audio channel player.');
				}
			}
		} catch (e) {
			toast.error('Encountered an error attempting to play an audio file.')
		}
	}, [props.file, props.channel]);
	
	return (
		<React.Fragment>
			<div ref={container} style={getContainerStyle()}></div>
		</React.Fragment>
	);
	
	function getContext(): AudioContext {
		const ctx = window.AudioContext ?? (window as any).webkitAudioContext;
		return new ctx();
	}
	
	function getContainerStyle() {
		if (!props.width)
			return {};
		
		return { width: props.width };
	}
}

interface Props {
    file: File;
    channel: 1 | 2;
    width?: string | number;
}
